// src/components/ClientDetails.js
// Client B2C — /clients/:clientId. One end customer: who it is and how to
// reach it (profile, what the voice agent remembers), what needs a check
// (an unverified address, an order with a parcel problem), then its tickets,
// its orders, its calls and one history feed. Views (?tab=): tickets ·
// orders · calls · history; the ticket page is ?tpage=.
// API: GET /api/clients (the list, when the page is opened by link),
// GET /api/clients/{id}/tickets, GET /api/clients/{id}/voice, PATCH
// /api/clients/{id}, PATCH /api/clients/{id}/vip, POST /api/clients/{id}/send-email,
// POST /api/clients/writer-assistant, and — read-only, by the customer's
// e-mail — GET /api/commerce/orders?q=<email>.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, ChevronRight, CircleAlert, CircleCheck, Crown, History, Mail, Paperclip, Pencil, Phone, RefreshCw,
  Send, ShoppingCart, Sparkles, Ticket, Trash2, TriangleAlert, UserRound,
} from 'lucide-react';
import {
  Badge, Banner, Card, EmptyState, Field, IconChip, Kpi, Modal, Tabs, Timeline, Toast, initialsOf,
} from '../design/ui';
import { getStatusLabel } from '../constants/ticketStatuses';
import { formatCallFull, formatDuration, outcomeChips } from '../utils/voiceFormat';
import { statusTone } from './clientsB2B';
import {
  B2C_FIELDS, addressNeedsReview, addressText, buildCustomerUpdatePayload, customerAttention, customerHistory,
  customerOrderSummary, customerTicketSummary, formFromCustomer, sourceLabel,
} from './clientsB2C';
import { formatCount, formatDay, formatMoney, formatRelative, formatShortDay, formatStamp, nextParams } from './clientsShared';
import './ClientDetails.css';

const CLIENT_FETCH_BATCH_SIZE = 200;
const TICKETS_PAGE_SIZE = 25;
const ORDERS_SIZE = 25;
const MAX_FILES = 10;
const TABS = ['tickets', 'orders', 'calls', 'history'];

// Order status → badge tone (commerce/api.js STATUS_TONE vocabulary).
const ORDER_TONE = { preparing: 'info', shipped: 'accent', delivered: 'success', incident: 'danger', returned: 'warning' };
const ORDER_STATUS_FR = {
  pending: 'En attente', preparing: 'En préparation', shipped: 'Expédiée', delivered: 'Livrée',
  incident: 'Incident', returned: 'Retournée', cancelled: 'Annulée',
};
const CHIP_TONE = { action: 'success', escalation: 'warning', lookup: undefined };

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'tickets';
  const pageParam = parseInt(searchParams.get('tpage') || '1', 10);
  const ticketsPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const updateParams = useCallback((updates, { replace = true } = {}) => {
    setSearchParams((previous) => nextParams(previous, updates), { replace });
  }, [setSearchParams]);

  const [client, setClient] = useState(location.state?.client || null);
  const [clientLoading, setClientLoading] = useState(!client);
  const [clientError, setClientError] = useState(null);

  const [tickets, setTickets] = useState(null); // null = loading
  const [ticketsError, setTicketsError] = useState(null);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);

  const [voiceData, setVoiceData] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(true);
  const [orders, setOrders] = useState(undefined); // undefined = not asked / loading, null = unavailable

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', content: '', senderEmail: '' });
  const [emailFiles, setEmailFiles] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [aiAssistantLoading, setAiAssistantLoading] = useState(false);
  const [vipUpdating, setVipUpdating] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(formFromCustomer(null));
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const authHeaders = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!authHeaders) navigate('/');
  }, [authHeaders, navigate]);

  // ---- fetch ------------------------------------------------------------------

  const fetchClientDetails = useCallback(async () => {
    if (client) {
      setClientLoading(false);
      return;
    }
    if (!authHeaders) return;
    setClientLoading(true);
    setClientError(null);
    let skip = 0;
    try {
      // The API has no « get one »: page through the list until the id shows.
      while (true) {
        const response = await axios.get('/api/clients', {
          headers: authHeaders,
          params: { skip, limit: CLIENT_FETCH_BATCH_SIZE },
        });
        const payload = Array.isArray(response.data) ? response.data : [];
        const found = payload.find((entry) => entry.id === clientId);
        if (found) {
          setClient(found);
          break;
        }
        if (payload.length < CLIENT_FETCH_BATCH_SIZE) {
          setClientError(t('clientDetails.clientNotFound', 'Client non trouvé'));
          break;
        }
        skip += CLIENT_FETCH_BATCH_SIZE;
      }
    } catch (err) {
      setClientError(err.response?.data?.detail || err.message || t('clientDetails.loadClientError', 'Échec du chargement des détails du client'));
    } finally {
      setClientLoading(false);
    }
  }, [authHeaders, client, clientId, t]);

  const fetchTickets = useCallback(async () => {
    if (!authHeaders) return;
    setTickets(null);
    setTicketsError(null);
    try {
      const response = await axios.get(`/api/clients/${clientId}/tickets`, {
        headers: authHeaders,
        params: { skip: (ticketsPage - 1) * TICKETS_PAGE_SIZE, limit: TICKETS_PAGE_SIZE },
      });
      const payload = Array.isArray(response.data) ? response.data : [];
      setTickets(payload);
      setTicketsHasMore(payload.length === TICKETS_PAGE_SIZE);
    } catch (err) {
      setTicketsError(err.response?.data?.detail || err.message || t('clientDetails.loadTicketsError', 'Échec du chargement des tickets'));
      setTickets([]);
      setTicketsHasMore(false);
    }
  }, [authHeaders, clientId, ticketsPage, t]);

  const fetchVoice = useCallback(async () => {
    if (!authHeaders) return;
    setVoiceLoading(true);
    try {
      const response = await axios.get(`/api/clients/${clientId}/voice`, { headers: authHeaders });
      setVoiceData(response.data || null);
    } catch (err) {
      // Voice data is optional (e-mail-only customers have none).
      setVoiceData(null);
    } finally {
      setVoiceLoading(false);
    }
  }, [authHeaders, clientId]);

  const customerEmail = client?.email || '';
  const fetchOrders = useCallback(async () => {
    if (!authHeaders || !customerEmail) return;
    setOrders(undefined);
    try {
      const response = await axios.get('/api/commerce/orders', {
        headers: authHeaders,
        params: { q: customerEmail, size: ORDERS_SIZE },
      });
      setOrders(response.data || { items: [], total: 0 });
    } catch {
      setOrders(null);
    }
  }, [authHeaders, customerEmail]);

  useEffect(() => { fetchClientDetails(); }, [fetchClientDetails]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { fetchVoice(); }, [fetchVoice]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRefresh = () => {
    fetchTickets();
    fetchVoice();
    fetchOrders();
  };

  const openTicket = (ticketId) => navigate(`/tickets/${ticketId}/messages`);
  const setTab = (next) => updateParams({ tab: next === 'tickets' ? null : next });

  // ---- e-mail -------------------------------------------------------------------

  const openEmailModal = () => {
    setEmailError(null);
    setIsEmailModalOpen(true);
  };

  const closeEmailModal = (force = false) => {
    if (emailSending && force !== true) return;
    setIsEmailModalOpen(false);
    setEmailForm({ subject: '', content: '', senderEmail: '' });
    setEmailFiles([]);
    setEmailError(null);
  };

  const handleEmailInputChange = (event) => {
    const { name, value } = event.target;
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;
    const availableSlots = MAX_FILES - emailFiles.length;
    if (availableSlots <= 0) {
      setEmailError(t('clientDetails.maxFilesAllowed', 'Maximum 10 fichiers autorisés'));
      return;
    }
    const processedFiles = await Promise.all(
      selectedFiles.slice(0, availableSlots).map(async (file) => {
        const base64Content = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.readAsDataURL(file);
        });
        return { name: file.name, mime_type: file.type || 'application/octet-stream', content: base64Content, size: file.size };
      }),
    );
    setEmailFiles((prev) => [...prev, ...processedFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => setEmailFiles((prev) => prev.filter((_, i) => i !== index));

  const handleAiAssistant = async () => {
    if (!authHeaders || !emailForm.content.trim()) {
      setEmailError(t('clientDetails.enterContentToImprove', 'Veuillez saisir du contenu à améliorer'));
      return;
    }
    setAiAssistantLoading(true);
    setEmailError(null);
    try {
      const response = await axios.post(
        '/api/clients/writer-assistant',
        { text: emailForm.content, client_email: client?.email || undefined },
        { headers: authHeaders },
      );
      if (response.data?.response) setEmailForm((prev) => ({ ...prev, content: response.data.response }));
    } catch (err) {
      setEmailError(err.response?.data?.detail || err.message || t('clientDetails.aiAssistantFailed', 'L\'assistant IA a échoué'));
    } finally {
      setAiAssistantLoading(false);
    }
  };

  const handleSendEmail = async (event) => {
    if (event) event.preventDefault();
    if (!authHeaders || !clientId) return;
    const trimmedSubject = emailForm.subject.trim();
    const trimmedContent = emailForm.content.trim();
    const trimmedSender = emailForm.senderEmail.trim();
    if (!trimmedSubject || !trimmedContent) {
      setEmailError(t('clientDetails.subjectContentRequired', 'L\'objet et le contenu sont requis'));
      return;
    }
    setEmailSending(true);
    setEmailError(null);
    try {
      const payload = { subject: trimmedSubject, content: trimmedContent, sender_email: trimmedSender || undefined };
      if (emailFiles.length > 0) {
        payload.files = emailFiles.map((file) => ({ name: file.name, mime_type: file.mime_type, content: file.content }));
      }
      await axios.post(`/api/clients/${clientId}/send-email`, payload, { headers: authHeaders });
      setToast({ tone: 'success', text: t('clientDetails.emailSentSuccess', 'E-mail envoyé avec succès') });
      if (ticketsPage !== 1) updateParams({ tpage: null });
      else fetchTickets();
      closeEmailModal(true);
    } catch (err) {
      setEmailError(err.response?.data?.detail || err.message || t('clientDetails.sendEmailError', 'Échec de l\'envoi de l\'e-mail'));
    } finally {
      setEmailSending(false);
    }
  };

  // ---- profile ------------------------------------------------------------------

  const openEditModal = () => {
    setEditError(null);
    setEditForm(formFromCustomer(client));
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    if (!editSaving) setIsEditOpen(false);
  };

  const handleSaveClientEdit = async (event) => {
    if (event) event.preventDefault();
    if (!authHeaders || !clientId) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const response = await axios.patch(`/api/clients/${clientId}`, buildCustomerUpdatePayload(editForm), { headers: authHeaders });
      setClient(response.data);
      setIsEditOpen(false);
      setToast({ tone: 'success', text: t('clientsUi.customer.saved', 'Fiche client enregistrée.') });
    } catch (err) {
      setEditError(err.response?.data?.detail || err.message || t('clientsUi.customer.saveError', 'Échec de la mise à jour du client.'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleVip = async () => {
    if (!authHeaders || !clientId || !client) return;
    setVipUpdating(true);
    try {
      const newVipStatus = !client.is_vip;
      await axios.patch(`/api/clients/${clientId}/vip`, { is_vip: newVipStatus }, { headers: authHeaders });
      setClient((prev) => ({ ...prev, is_vip: newVipStatus, updated_at: new Date().toISOString() }));
      setToast({
        tone: 'success',
        text: newVipStatus ? t('clientsUi.customer.vipOn', 'Client marqué VIP.') : t('clientsUi.customer.vipOff', 'Statut VIP retiré.'),
      });
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || err.message || t('clientDetails.updateVipError', 'Échec de la mise à jour du statut VIP') });
    } finally {
      setVipUpdating(false);
    }
  };

  // ---- derived ----------------------------------------------------------------------

  const ticketSummary = useMemo(() => customerTicketSummary(tickets), [tickets]);
  const orderSummary = useMemo(() => customerOrderSummary(orders), [orders]);
  const calls = useMemo(() => voiceData?.conversations || [], [voiceData]);
  const memory = voiceData?.memory || null;
  const hasVoice = Boolean(memory || calls.length);
  const attention = useMemo(() => customerAttention({ client, orders }), [client, orders]);
  const history = useMemo(
    () => customerHistory({ client, tickets, orders: orders?.items, calls }),
    [client, tickets, orders, calls],
  );

  if (!authHeaders) return null;

  const orderStatus = (status) => t(`commerceOrders.statuses.${status}`, ORDER_STATUS_FR[status] || status || '—');

  // ---- zones --------------------------------------------------------------------------

  const renderHero = () => {
    if (clientLoading && !client) {
      return (
        <section className="u-card customer-v2__hero" aria-busy="true">
          <div className="customer-v2__identity">
            <span className="u-skeleton customer-v2__avatar-skeleton" />
            <div className="customer-v2__identity-text">
              <span className="u-skeleton customer-v2__line-skeleton" />
              <span className="u-skeleton customer-v2__title-skeleton" />
            </div>
          </div>
        </section>
      );
    }
    if (!client) return null;
    const tabs = [
      { key: 'tickets', label: t('clientDetails.tickets', 'Tickets'), icon: Ticket, count: tickets ? `${formatCount(ticketSummary.shown)}${ticketsHasMore ? '+' : ''}` : undefined },
      client.email && orders !== null && { key: 'orders', label: t('clientsUi.customer.tabOrders', 'Commandes'), icon: ShoppingCart, count: orderSummary ? formatCount(orderSummary.total) : undefined },
      hasVoice && { key: 'calls', label: t('clientsUi.customer.tabCalls', 'Appels'), icon: Phone, count: formatCount(calls.length) },
      { key: 'history', label: t('clientsUi.customer.tabHistory', 'Historique'), icon: History },
    ].filter(Boolean);
    return (
      <section className="u-card customer-v2__hero">
        <div className="customer-v2__identity">
          <span className="u-avatar u-avatar--lg" aria-hidden="true">{initialsOf(client.name)}</span>
          <div className="customer-v2__identity-text">
            <nav className="u-breadcrumb" aria-label={t('clientsUi.breadcrumb', 'Fil d’Ariane')}>
              <Link to="/clients">{t('clientsPage.titleB2C', 'Clients B2C')}</Link>
              <ChevronRight className="customer-v2__crumb-sep" aria-hidden="true" />
            </nav>
            <h1 className="u-page-title">{client.name}</h1>
            <div className="customer-v2__meta">
              {client.is_vip && <Badge tone="warning" icon={Crown}>{t('clientDetails.vipClient', 'Client VIP')}</Badge>}
              {client.email && <span>{client.email}</span>}
              {client.phone_number && <span className="u-num">{client.phone_number}</span>}
              {client.source && <span className="u-tag customer-v2__source">{sourceLabel(t, client.source)}</span>}
            </div>
          </div>
          <div className="customer-v2__actions">
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={handleRefresh}
              disabled={tickets === null}
              title={t('clientDetails.refreshTickets', 'Actualiser les tickets')}
              aria-label={t('clientDetails.refreshTickets', 'Actualiser les tickets')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button type="button" className="u-btn u-btn--secondary" onClick={openEditModal}>
              <Pencil aria-hidden="true" />
              {t('common.edit', 'Modifier')}
            </button>
            <button type="button" className="u-btn u-btn--secondary" onClick={handleToggleVip} disabled={vipUpdating}>
              <Crown aria-hidden="true" />
              {vipUpdating
                ? t('clientDetails.updating', 'Mise à jour...')
                : client.is_vip ? t('clientDetails.removeVip', 'Retirer VIP') : t('clientDetails.markAsVip', 'Marquer comme VIP')}
            </button>
            <button
              type="button"
              className="u-btn u-btn--primary"
              onClick={openEmailModal}
              disabled={!client.email}
              title={client.email ? undefined : t('clientsUi.customer.noEmailToSend', 'Ce client n’a pas d’adresse e-mail.')}
            >
              <Send aria-hidden="true" />
              {t('clientDetails.sendEmail', 'Envoyer un e-mail')}
            </button>
          </div>
        </div>
        <Tabs label={client.name} active={tab} onChange={setTab} tabs={tabs} />
      </section>
    );
  };

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('clientsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="customer-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => (
            <li className="u-row" key={item.key}>
              <Badge tone={item.tone} dot>
                {item.tone === 'danger' ? t('clientsUi.attention.critical', 'Bloquant') : t('clientsUi.attention.watch', 'À surveiller')}
              </Badge>
              <div className="u-row__main">
                {item.kind === 'address' ? (
                  <>
                    <div className="u-row__title">{t('clientsUi.customer.attnAddress', 'Adresse à vérifier')}</div>
                    <div className="u-row__meta u-row__meta--wrap">
                      {t('clientsUi.customer.attnAddressDetail', 'L’agent vocal n’a pas pu vérifier « {{address}} ». Corrigez-la : une adresse saisie à la main est considérée comme vérifiée.', { address: addressText(client.address) })}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="u-row__title">
                      {t('clientsUi.customer.attnOrder', 'Colis en difficulté sur la commande {{number}}', { number: item.order.number || item.order.external_id })}
                    </div>
                    <div className="u-row__meta u-row__meta--wrap">
                      {(item.order.problems || []).map((problem) => [problem.carrier, problem.tracking_number, problem.detail].filter(Boolean).join(' · ')).join(' — ')
                        || t('clientsUi.customer.attnOrderDetail', 'Le suivi du colis signale un problème.')}
                    </div>
                  </>
                )}
              </div>
              <div className="u-row__side">
                {item.kind === 'address' ? (
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={openEditModal}>
                    {t('clientsUi.customer.fixAddress', 'Corriger')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                ) : (
                  <Link className="u-btn u-btn--secondary u-btn--sm" to={`/commerce/orders?order=${item.order.id}`}>
                    {t('clientsUi.see', 'Voir')}
                    <ChevronRight aria-hidden="true" />
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (!client) return null;
    const lastCall = memory?.last_call_at || calls[0]?.started_at;
    return (
      <div className="u-kpi-row">
        {tickets === null ? <span className="u-skeleton customer-v2__kpi-skeleton" aria-hidden="true" /> : !ticketsError && (
          <Kpi
            icon={Ticket}
            label={t('clientsUi.customer.kpiTickets', 'Tickets')}
            value={`${formatCount(ticketSummary.shown)}${ticketsHasMore ? '+' : ''}`}
            context={ticketSummary.shown
              ? t('clientsUi.customer.kpiTicketsContext', '{{open}} en cours · {{ai}} résolus par l’IA', { open: ticketSummary.open, ai: ticketSummary.byAi })
              : t('clientsUi.customer.kpiTicketsNone', 'aucun ticket pour l’instant')}
            contextTone={ticketSummary.open ? 'warning' : undefined}
            active={tab === 'tickets'}
            onClick={() => setTab('tickets')}
          />
        )}
        {client.email && orders === undefined && <span className="u-skeleton customer-v2__kpi-skeleton" aria-hidden="true" />}
        {orderSummary && (
          <Kpi
            icon={ShoppingCart}
            tone={orderSummary.problems.length ? 'danger' : undefined}
            label={t('clientsUi.customer.kpiOrders', 'Commandes')}
            value={formatCount(orderSummary.total)}
            context={orderSummary.total
              ? (orderSummary.amount !== null
                ? t('clientsUi.customer.kpiOrdersAmount', '{{amount}} sur les {{count}} dernières', { amount: formatMoney(orderSummary.amount, orderSummary.currency), count: orderSummary.shown })
                : t('clientsUi.customer.kpiOrdersLast', 'dernière le {{date}}', { date: formatDay(orderSummary.lastPlaced) }))
              : t('clientsUi.customer.kpiOrdersNone', 'aucune commande à cette adresse')}
            active={tab === 'orders'}
            onClick={() => setTab('orders')}
          />
        )}
        {hasVoice && (
          <Kpi
            icon={Phone}
            label={t('clientsUi.customer.kpiCalls', 'Appels')}
            value={formatCount(memory?.call_count ?? calls.length)}
            context={lastCall ? t('clientsUi.customer.kpiCallsLast', 'dernier : {{when}}', { when: formatRelative(lastCall) }) : undefined}
            active={tab === 'calls'}
            onClick={() => setTab('calls')}
          />
        )}
        {client.created_at && (
          <Kpi
            icon={CalendarDays}
            tone="neutral"
            label={t('clientsUi.customer.kpiSince', 'Client depuis')}
            value={formatShortDay(client.created_at)}
            title={formatStamp(client.created_at)}
            context={client.source
              ? t('clientsUi.customer.kpiSinceSource', 'venu par : {{source}}', { source: sourceLabel(t, client.source) })
              : undefined}
            active={tab === 'history'}
            onClick={() => setTab('history')}
          />
        )}
      </div>
    );
  };

  const renderTickets = () => {
    let body;
    if (tickets === null) {
      body = (
        <div className="customer-v2__skeleton" aria-busy="true" aria-label={t('clientDetails.loadingTickets', 'Chargement des tickets…')}>
          {Array.from({ length: 5 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    } else if (client && !client.email) {
      body = (
        <EmptyState
          icon={Mail}
          title={t('clientsUi.customer.noEmailTitle', 'Pas d’adresse e-mail')}
          action={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={openEditModal}>{t('clientsUi.customer.addEmail', 'Ajouter une adresse')}</button>}
        >
          {t('clientsUi.customer.noEmailHint', 'Les tickets d’un client sont retrouvés par son adresse e-mail : celui-ci n’en a pas.')}
        </EmptyState>
      );
    } else if (ticketsError) {
      body = (
        <div className="customer-v2__pad">
          <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchTickets}>{t('clientDetails.retry', 'Réessayer')}</button>}>
            {ticketsError}
          </Banner>
        </div>
      );
    } else if (!tickets.length) {
      body = (
        <EmptyState icon={Ticket} title={t('clientDetails.noTickets', 'Aucun ticket trouvé pour ce client')}>
          {t('clientsUi.customer.noTicketsHint', 'Ses tickets apparaîtront ici dès qu’il vous écrira depuis {{email}}.', { email: client?.email || '' })}
        </EmptyState>
      );
    } else {
      body = (
        <div className="u-table-scroll">
          <table className="u-table customer-v2__table">
            <thead>
              <tr>
                <th style={{ width: '52%' }}>{t('clientsUi.tickets.colTicket', 'Ticket')}</th>
                <th style={{ width: '32%' }}>{t('clientsUi.tickets.colStatus', 'Statut')}</th>
                <th style={{ width: '16%' }}>{t('clientsUi.tickets.colUpdated', 'Mis à jour')}</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const sub = [ticket.topic, ticket.brand?.name, ...(ticket.tags || [])].filter(Boolean).join(' · ');
                const byAi = ticket.resolution?.by === 'ai';
                return (
                  <tr
                    key={ticket.id}
                    className="customer-v2__row"
                    tabIndex={0}
                    onClick={() => openTicket(ticket.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openTicket(ticket.id);
                      }
                    }}
                  >
                    <td title={[ticket.subject, sub].filter(Boolean).join(' — ')}>
                      <span className="customer-v2__subject">{ticket.subject || t('clientDetails.noSubject', 'Aucun objet')}</span>
                      <span className="u-cell-sub">{sub || t('clientsUi.customer.openedOn', 'ouvert le {{date}}', { date: formatDay(ticket.created_at) })}</span>
                    </td>
                    <td>
                      <span className="customer-v2__badges">
                        <Badge tone={statusTone(ticket.status)} dot>{getStatusLabel(t, ticket.status || 'unknown')}</Badge>
                        {byAi && (
                          <span className="u-badge u-badge--accent customer-v2__ai" role="img" title={t('clientsUi.tickets.byAi', 'Résolu par l’agent IA')} aria-label={t('clientsUi.tickets.byAi', 'Résolu par l’agent IA')}>
                            <Sparkles aria-hidden="true" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="u-num" title={formatStamp(ticket.updated_at)}>{formatRelative(ticket.updated_at) || <span className="u-muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <section className="u-card">
        {body}
        {tickets && tickets.length > 0 && (ticketsPage > 1 || ticketsHasMore) && (
          <div className="u-pagination">
            <span>{t('clientsUi.pageN', 'Page {{page}}', { page: ticketsPage })}</span>
            <div className="u-pagination__pages">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={ticketsPage <= 1} onClick={() => updateParams({ tpage: ticketsPage - 1 <= 1 ? null : ticketsPage - 1 }, { replace: false })}>
                {t('clientDetails.previous', 'Précédent')}
              </button>
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={!ticketsHasMore} onClick={() => updateParams({ tpage: ticketsPage + 1 }, { replace: false })}>
                {t('clientDetails.next', 'Suivant')}
              </button>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderOrders = () => {
    if (orders === undefined) {
      return <section className="u-card"><div className="customer-v2__skeleton" aria-busy="true">{Array.from({ length: 3 }, (_, i) => <span key={i} className="u-skeleton" />)}</div></section>;
    }
    const items = orders?.items || [];
    return (
      <Card
        icon={ShoppingCart}
        title={t('clientsUi.customer.ordersTitle', 'Ses commandes')}
        description={t('clientsUi.customer.ordersDesc', 'Les commandes de vos boutiques et de l’API passées avec {{email}}.', { email: client.email })}
        actions={(
          <Link className="u-btn u-btn--ghost u-btn--sm" to={`/commerce/orders?q=${encodeURIComponent(client.email)}`}>
            {t('clientsUi.customer.ordersOpen', 'Ouvrir dans Commandes')}
            <ChevronRight aria-hidden="true" />
          </Link>
        )}
        flush
      >
        {!items.length ? (
          <EmptyState icon={ShoppingCart} title={t('clientsUi.customer.ordersEmpty', 'Aucune commande à cette adresse')}>
            {t('clientsUi.customer.ordersEmptyHint', 'Les commandes sont retrouvées par l’adresse e-mail saisie à l’achat.')}
          </EmptyState>
        ) : (
          <div className="u-table-scroll">
            <table className="u-table customer-v2__table">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>{t('clientsUi.customer.colOrder', 'Commande')}</th>
                  <th style={{ width: '16%' }}>{t('clientsUi.customer.colPlaced', 'Passée le')}</th>
                  <th style={{ width: '18%' }}>{t('clientsUi.tickets.colStatus', 'Statut')}</th>
                  <th style={{ width: '14%' }} className="customer-v2__num">{t('clientsUi.customer.colTotal', 'Montant')}</th>
                  <th style={{ width: '32%' }}>{t('clientsUi.customer.colParcel', 'Colis')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((order) => {
                  const shipment = (order.shipments || [])[0];
                  const openOrder = () => navigate(`/commerce/orders?order=${order.id}`);
                  return (
                    <tr
                      key={order.id}
                      className="customer-v2__row"
                      tabIndex={0}
                      onClick={openOrder}
                      onKeyDown={(e) => { if (e.key === 'Enter') openOrder(); }}
                    >
                      <td>
                        <span className="customer-v2__subject">{order.number || order.external_id}</span>
                        {order.source && <span className="u-cell-sub">{t(`commerceOrders.sources.${order.source}`, order.source)}</span>}
                      </td>
                      <td className="u-num" title={formatStamp(order.placed_at)}>{formatShortDay(order.placed_at) || <span className="u-muted">—</span>}</td>
                      <td>
                        {order.problem_open ? (
                          <Badge tone="danger" icon={CircleAlert} title={t('clientsUi.customer.problem', 'Colis en difficulté')}>{orderStatus(order.status)}</Badge>
                        ) : (
                          <Badge tone={ORDER_TONE[order.status]} dot>{orderStatus(order.status)}</Badge>
                        )}
                      </td>
                      <td className="customer-v2__num">{order.total !== null && order.total !== undefined ? formatMoney(order.total, order.currency) : <span className="u-muted">—</span>}</td>
                      <td title={shipment ? [shipment.carrier, shipment.tracking_number].filter(Boolean).join(' · ') : ''}>
                        {shipment ? (
                          <>
                            <span className="customer-v2__mono">{shipment.tracking_number}</span>
                            {shipment.carrier && <span className="u-cell-sub">{shipment.carrier}{order.shipments.length > 1 ? ` · +${order.shipments.length - 1}` : ''}</span>}
                          </>
                        ) : <span className="u-muted">{t('clientsUi.customer.notShipped', 'pas encore expédiée')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {orderSummary && orderSummary.total > orderSummary.shown && (
          <div className="customer-v2__more">
            {t('clientsUi.customer.ordersMore', '{{shown}} commandes affichées sur {{total}} : ouvrez Commandes pour toutes les voir.', { shown: orderSummary.shown, total: orderSummary.total })}
          </div>
        )}
      </Card>
    );
  };

  const renderCalls = () => (
    <Card
      icon={Phone}
      title={t('voiceTickets.clientSection.calls', 'Appels')}
      description={voiceData?.phone_number ? t('clientsUi.customer.callsDesc', 'Les appels reçus par l’agent vocal depuis le {{phone}}.', { phone: voiceData.phone_number }) : undefined}
      flush
    >
      {!calls.length ? (
        <EmptyState icon={Phone} title={t('voiceTickets.clientSection.noCalls', 'Aucune conversation vocale.')} />
      ) : (
        <ul className="u-list">
          {calls.map((call) => {
            const chips = outcomeChips(call.tool_names, t);
            const open = () => navigate(`/voice-tickets/${encodeURIComponent(call.call_id)}`);
            return (
              <li key={call.call_id} className="u-row customer-v2__call">
                <IconChip icon={Phone} tone="neutral" size="sm" />
                <div className="u-row__main">
                  <div className="u-row__title">
                    {formatCallFull(call.started_at)}
                    {chips.length
                      ? chips.map((chip) => <Badge key={chip.name} tone={CHIP_TONE[chip.kind]}>{chip.label}</Badge>)
                      : <Badge tone="info">{t('voiceTickets.facetInfo', 'Info seule')}</Badge>}
                  </div>
                  <div className="u-row__meta">
                    {t('clientsUi.customer.callMeta', '{{duration}} · {{turns}} échanges', { duration: formatDuration(call.duration_seconds), turns: call.turn_count ?? 0 })}
                  </div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={open}>
                    {t('clientsUi.customer.listen', 'Ouvrir l’appel')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );

  const historyItem = (event) => {
    switch (event.kind) {
      case 'ticket':
        return { icon: Ticket, tone: 'accent', title: t('clientsUi.customer.histTicket', 'Ticket ouvert : {{subject}}', { subject: event.ticket.subject || t('clientDetails.noSubject', 'Aucun objet') }), meta: [event.ticket.topic, event.ticket.brand?.name].filter(Boolean).join(' · ') };
      case 'resolved':
        return {
          icon: CircleCheck,
          tone: 'success',
          title: event.ticket.resolution?.by === 'ai'
            ? t('clientsUi.customer.histResolvedAi', 'Ticket résolu par l’agent IA : {{subject}}', { subject: event.ticket.subject || '' })
            : t('clientsUi.customer.histResolved', 'Ticket résolu : {{subject}}', { subject: event.ticket.subject || '' }),
        };
      case 'order':
        return {
          icon: ShoppingCart,
          tone: event.order.problem_open ? 'danger' : undefined,
          title: t('clientsUi.customer.histOrder', 'Commande {{number}} passée', { number: event.order.number || event.order.external_id }),
          meta: [orderStatus(event.order.status), event.order.total !== null && event.order.total !== undefined ? formatMoney(event.order.total, event.order.currency) : null].filter(Boolean).join(' · '),
        };
      case 'call': {
        const chips = outcomeChips(event.call.tool_names, t);
        return {
          icon: Phone,
          title: t('clientsUi.customer.histCall', 'Appel de {{duration}}', { duration: formatDuration(event.call.duration_seconds) }),
          meta: chips.map((chip) => chip.label).join(' · '),
        };
      }
      default:
        return { icon: UserRound, title: t('clientsUi.customer.histCreated', 'Fiche client créée'), meta: client?.source ? sourceLabel(t, client.source) : undefined };
    }
  };

  const renderHistory = () => (
    <Card
      icon={History}
      title={t('clientsUi.customer.historyTitle', 'Historique')}
      description={t('clientsUi.customer.historyDesc', 'Tickets, commandes et appels, du plus récent au plus ancien — d’après ce que la page a chargé.')}
    >
      {!history.length ? (
        <p className="u-muted customer-v2__note">{t('clientsUi.customer.historyEmpty', 'Rien à montrer pour l’instant.')}</p>
      ) : (
        <Timeline
          items={history.slice(0, 30).map((event) => {
            const item = historyItem(event);
            return { key: event.key, icon: item.icon, tone: item.tone, title: item.title, meta: item.meta || undefined, time: formatStamp(event.at) };
          })}
        />
      )}
    </Card>
  );

  const renderProfile = () => {
    const address = addressText(client.address);
    const facts = [
      { label: t('clientsUi.customer.factEmail', 'E-mail'), value: client.email },
      { label: t('clientsUi.customer.factPhone', 'Téléphone'), value: client.phone_number },
      {
        label: t('clientsUi.customer.factAddress', 'Adresse'),
        value: address ? (
          <span className="customer-v2__address">
            {address}
            {addressNeedsReview(client) && <Badge tone="warning" dot>{t('clientsUi.b2c.toCheck', 'À vérifier')}</Badge>}
            {client.address_status === 'verified' && <Badge tone="success">{t('clientsUi.customer.verified', 'Vérifiée')}</Badge>}
          </span>
        ) : null,
      },
      { label: t('clientsPage.source', 'Source'), value: client.source ? sourceLabel(t, client.source) : null },
      { label: t('clientsUi.customer.factCreated', 'Créé le'), value: formatStamp(client.created_at) },
      { label: t('clientsUi.customer.factUpdated', 'Mis à jour'), value: formatStamp(client.updated_at) },
    ];
    return (
      <Card
        icon={UserRound}
        title={t('clientsUi.customer.profileTitle', 'Profil')}
        actions={(
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={openEditModal}>
            <Pencil aria-hidden="true" />
            {t('common.edit', 'Modifier')}
          </button>
        )}
      >
        <dl className="customer-v2__facts">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt className="u-overline">{fact.label}</dt>
              <dd>{fact.value || <span className="u-muted">{t('clientsUi.preview.none', 'Non renseigné')}</span>}</dd>
            </div>
          ))}
        </dl>
      </Card>
    );
  };

  const renderMemory = () => {
    if (voiceLoading) return <span className="u-skeleton customer-v2__card-skeleton" aria-hidden="true" />;
    if (!memory) return null;
    // What the agent heard, only where it differs from the profile above.
    const differs = (heard, known) => heard && String(heard).trim().toLowerCase() !== String(known || '').trim().toLowerCase();
    const facts = [
      differs(memory.caller_name, client.name) && { label: t('voiceTickets.detail.name', 'Nom'), value: memory.caller_name },
      differs(memory.email, client.email) && { label: t('voiceTickets.detail.email', 'E-mail'), value: memory.email },
      differs(memory.postal_address, addressText(client.address)) && { label: t('voiceTickets.detail.address', 'Adresse'), value: memory.postal_address },
      { label: t('voiceTickets.detail.callCount', 'Appels'), value: t('voiceTickets.detail.callsTotal', '{{n}} au total', { n: memory.call_count ?? 0 }) },
    ].filter(Boolean);
    return (
      <Card
        icon={Sparkles}
        title={t('clientsUi.customer.memoryTitle', 'Ce que l’agent vocal retient')}
        description={t('clientsUi.customer.memoryDesc', 'Sa mémoire de l’appelant, relue à chaque appel.')}
      >
        <div className="customer-v2__memory">
          {memory.summary && <p className="customer-v2__summary">{memory.summary}</p>}
          <dl className="customer-v2__facts">
            {facts.map((fact) => (
              <div key={fact.label}>
                <dt className="u-overline">{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
          {Array.isArray(memory.preferences) && memory.preferences.length > 0 && (
            <div>
              <div className="u-overline">{t('voiceTickets.detail.preferences', 'Préférences')}</div>
              <div className="customer-v2__tags">{memory.preferences.map((pref) => <span key={pref} className="u-tag">{pref}</span>)}</div>
            </div>
          )}
          {Array.isArray(memory.past_requests) && memory.past_requests.length > 0 && (
            <div>
              <div className="u-overline">{t('voiceTickets.detail.pastRequests', 'Demandes précédentes')}</div>
              <div className="customer-v2__tags">{memory.past_requests.map((request) => <span key={request} className="u-tag">{request}</span>)}</div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="u-page customer-v2">
      {clientError && !client ? (
        <section className="u-card">
          <EmptyState
            icon={CircleAlert}
            title={clientError}
            action={<Link className="u-btn u-btn--secondary u-btn--sm" to="/clients">{t('clientDetails.backToB2C', 'Retour aux clients B2C')}</Link>}
          />
        </section>
      ) : (
        <>
          {renderHero()}
          {client && (
            <div className="u-well">
              {renderAttention()}
              {renderKpis()}
              <div className="customer-v2__layout">
                <div role="tabpanel" className="customer-v2__main">
                  {tab === 'tickets' && renderTickets()}
                  {tab === 'orders' && client.email && renderOrders()}
                  {tab === 'calls' && renderCalls()}
                  {tab === 'history' && renderHistory()}
                </div>
                <aside className="customer-v2__aside">
                  {renderProfile()}
                  {renderMemory()}
                </aside>
              </div>
            </div>
          )}
        </>
      )}

      {isEditOpen && (
        <Modal
          icon={Pencil}
          title={t('clientsUi.customer.editTitle', 'Modifier le client')}
          description={t('clientsUi.customer.editDesc', 'Une adresse saisie ici est considérée comme vérifiée.')}
          onClose={closeEditModal}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={closeEditModal} disabled={editSaving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="submit" form="customer-edit-form" className="u-btn u-btn--primary" disabled={editSaving}>
                {editSaving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
              </button>
            </>
          )}
        >
          <form id="customer-edit-form" className="u-form" onSubmit={handleSaveClientEdit} noValidate>
            {editError && <Banner tone="danger">{editError}</Banner>}
            <div className="customer-v2__form-grid">
              {B2C_FIELDS.map((field) => (
                <div key={field.key} className={field.full ? 'customer-v2__field--full' : undefined}>
                  <Field label={t(`clientsUi.b2cFields.${field.key}`, field.label)} htmlFor={`client-edit-${field.key}`}>
                    <input
                      id={`client-edit-${field.key}`}
                      name={field.key}
                      type={field.type}
                      className="u-input"
                      value={editForm[field.key]}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      autoComplete={field.autoComplete}
                    />
                  </Field>
                </div>
              ))}
            </div>
          </form>
        </Modal>
      )}

      {isEmailModalOpen && (
        <Modal
          wide
          icon={Send}
          title={t('clientDetails.sendEmailTo', 'Envoyer un e-mail à {{name}}', { name: client?.name || '' })}
          description={t('clientsUi.customer.emailDesc', 'Part de votre boîte Outlook vers {{email}} et rejoint ses tickets.', { email: client?.email || '' })}
          onClose={closeEmailModal}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={closeEmailModal} disabled={emailSending}>
                {t('clientDetails.cancel', 'Annuler')}
              </button>
              <button type="submit" form="customer-email-form" className="u-btn u-btn--primary" disabled={emailSending}>
                <Send aria-hidden="true" />
                {emailSending ? t('clientDetails.sending', 'Envoi…') : t('clientDetails.sendEmail', 'Envoyer un e-mail')}
              </button>
            </>
          )}
        >
          <form id="customer-email-form" className="u-form" onSubmit={handleSendEmail} noValidate>
            {emailError && <Banner tone="danger">{emailError}</Banner>}
            <Field label={t('clientDetails.subject', 'Objet')} htmlFor="client-email-subject">
              <input id="client-email-subject" name="subject" type="text" className="u-input" value={emailForm.subject} onChange={handleEmailInputChange} placeholder={t('clientDetails.enterSubject', 'Saisissez l\'objet')} />
            </Field>
            <div className="u-field">
              <div className="customer-v2__content-head">
                <label className="u-label" htmlFor="client-email-content">{t('clientDetails.content', 'Contenu')}</label>
                <button
                  type="button"
                  className="u-btn u-btn--ghost u-btn--sm"
                  onClick={handleAiAssistant}
                  disabled={aiAssistantLoading || !emailForm.content.trim()}
                  title={t('clientDetails.improveWithAi', 'Améliorer avec l\'IA')}
                >
                  <Sparkles aria-hidden="true" />
                  {aiAssistantLoading ? t('clientDetails.improving', 'Amélioration...') : t('clientDetails.improveWithAi', 'Améliorer avec l\'IA')}
                </button>
              </div>
              <textarea
                id="client-email-content"
                name="content"
                rows={8}
                className="u-textarea"
                value={emailForm.content}
                onChange={handleEmailInputChange}
                placeholder={t('clientDetails.writeMessage', 'Rédigez votre message')}
                disabled={aiAssistantLoading}
              />
            </div>
            <Field
              label={t('clientDetails.senderEmailOptional', 'E-mail de l\'expéditeur (optionnel)')}
              htmlFor="client-email-sender"
              help={t('clientsUi.customer.senderHelp', 'Vide : votre première boîte Outlook connectée.')}
            >
              <input id="client-email-sender" name="senderEmail" type="email" className="u-input" value={emailForm.senderEmail} onChange={handleEmailInputChange} placeholder={t('clientDetails.overrideOutlookSender', 'Remplacer l\'expéditeur Outlook par défaut')} />
            </Field>
            <div className="u-field">
              <span className="u-label">{t('clientDetails.attachments', 'Pièces jointes')}</span>
              <input ref={fileInputRef} type="file" id="client-email-files" multiple onChange={handleFileSelect} className="customer-v2__file-input" />
              <div className="customer-v2__files-head">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => fileInputRef.current?.click()} disabled={emailFiles.length >= MAX_FILES}>
                  <Paperclip aria-hidden="true" />
                  {t('clientDetails.addFiles', 'Ajouter des fichiers')}
                </button>
                <span className="u-help">{t('clientsUi.customer.filesCount', '{{count}} / {{max}} fichiers', { count: emailFiles.length, max: MAX_FILES })}</span>
              </div>
              {emailFiles.length > 0 && (
                <ul className="u-list customer-v2__files">
                  {emailFiles.map((file, index) => (
                    <li key={`${file.name}-${file.size}-${file.content.length}`} className="u-row customer-v2__file">
                      <div className="u-row__main">
                        <div className="u-row__title">{file.name}</div>
                        <div className="u-row__meta">{t('clientsUi.customer.fileSize', '{{size}} Ko', { size: (file.size / 1024).toFixed(1).replace('.', ',') })}</div>
                      </div>
                      <button
                        type="button"
                        className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                        onClick={() => handleRemoveFile(index)}
                        aria-label={t('clientsUi.customer.removeFile', 'Retirer {{name}}', { name: file.name })}
                        title={t('clientsUi.customer.removeFile', 'Retirer {{name}}', { name: file.name })}
                      >
                        <Trash2 aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </form>
        </Modal>
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default ClientDetails;
